import React, { useEffect, useRef } from "react";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  switchMap,
  delay,
} from "rxjs";
import PropTypes from "prop-types";
import xstream from "state-x-stream";
import eventEmitter from "../../../../components/eventEmitter";
import isWithinClass from "../../../../common/helpers/isWithinClass";
import Btn from "../../../../components/atoms/Btn";
import TimesIcon from "../../../../components/icons/Times";

function controller({ props$ }) {
  return {
    isClosed: merge(
      of(true),
      props$.pipe(
        map((x) => x.isOpen),
        filter(Boolean),
        map((x) => !x)
      ),
      props$.pipe(
        map((x) => !x.isOpen),
        distinctUntilChanged(),
        filter(Boolean),
        debounceTime(300)
      )
    ).pipe(distinctUntilChanged()),
  };
}

function Aside({ children, isClosed, forceClose, className, isOpen }) {
  const asideRef = useRef(null);

  useEffect(() => {
    if (isClosed) return () => {};

    function isWithin({ target }) {
      return (
        asideRef.current.contains(target) ||
        isWithinClass(target, "prevent-lose-focus")
      );
    }

    const sub = of(1)
      .pipe(
        delay(10),
        switchMap(() => eventEmitter.clickTracker$),
        filter(() => Boolean(asideRef.current)),
        map((ev) => !isWithin(ev)),
        filter(Boolean)
      )
      .subscribe(() => forceClose());

    return () => sub.unsubscribe();
  }, [isClosed, forceClose]);

  return (
    <div
      className={className}
      style={{
        transform: isOpen ? "translateX(0%)" : "translateX(120%)",
        opacity: isOpen ? 1 : 0,
      }}
      ref={asideRef}
    >
      {isClosed ? null : children}
      <div className="tw-absolute tw-top-0 tw-right-0 tw-p-1 tw-m-1">
        <Btn onSubmit={forceClose}>
          <TimesIcon
            className="hover:tw-text-black tw-cursor-pointer tw-fill-current tw-text-coolGray-600"
            size={16}
          />
        </Btn>
      </div>
    </div>
  );
}

Aside.propTypes = {
  isClosed: PropTypes.bool.isRequired,
  forceClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default xstream(controller)(Aside);
